import { Box, Divider, FlatList, HStack, Pressable, Text } from "native-base";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ApiServices from "../api/api";
import { API } from "../api/types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Colors from "../const/Colors";
import LoadingView from "./element/LoadingView";
import { Image } from "react-native";
import {
  ic_recycle_bin,
  ic_to_ai_magnifying_glass,
  ic_to_ai_report,
} from "../assets";
import AiUpdateInfoPopup from "./element/AiUpdateInfoPopup";
import { ppw } from "../util/LogUtil";
import PopupMessage from "./element/PopupMessage";
import { isAppError, toMessageError } from "../util/RequestUtil";
import { cloneDeep, isEmpty } from "lodash";
import AiLoadingView from "./element/AiLoadingView";
import { AxiosResponse } from "axios/index";

const AI_INFO_FORM_DATA: API.AiInfo[] = [
  {
    k: "sex",
    v: "",
    u: "",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_002",
      "ToshibaAIService_E_069",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_069"],
    min: 1,
    max: 2,
  },
  {
    k: "age",
    v: "",
    u: "歳",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_003",
      "ToshibaAIService_E_051",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_051"],
    min: 0,
    max: 999,
  },
  {
    k: "red_blood_cells_num",
    v: "",
    u: "万/μl",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_004",
      "ToshibaAIService_E_052",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_052"],
    min: 0,
    max: 1000,
  },
  {
    k: "hemoglobin_content",
    v: "",
    u: "g/dL",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_005",
      "ToshibaAIService_E_053",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_053"],
    min: 10,
    max: 30,
  },
  {
    k: "got_ast",
    v: "",
    u: "IU/L",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_006",
      "ToshibaAIService_E_054",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_054"],
    min: 1,
    max: 1000,
  },
  {
    k: "gpt_alt",
    v: "",
    u: "IU/L",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_007",
      "ToshibaAIService_E_055",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_055"],
    min: 1,
    max: 1000,
  },
  {
    k: "y_gpt",
    v: "",
    u: "IU/L",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_008",
      "ToshibaAIService_E_056",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_056"],
    min: 1,
    max: 2000,
  },
  {
    k: "creatinine",
    v: "",
    u: "mg/dL",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_009",
      "ToshibaAIService_E_057",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_057"],
    min: 0.1,
    max: 20,
  },
  {
    k: "fasting_blood_glucose",
    v: "",
    u: "mg/dL",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_010",
      "ToshibaAIService_E_058",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_058"],
    min: 20,
    max: 600,
  },
  {
    k: "hba1c",
    v: "",
    u: "%",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_011",
      "ToshibaAIService_E_059",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_059"],
    min: 3,
    max: 20,
  },
  {
    k: "neutral_fat",
    v: "",
    u: "mg/dL",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_012",
      "ToshibaAIService_E_060",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_060"],
    min: 10,
    max: 2000,
  },
  {
    k: "hdl_cholesterol",
    v: "",
    u: "mg/dL",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_013",
      "ToshibaAIService_E_061",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_061"],
    min: 10,
    max: 500,
  },
  {
    k: "ldl_cholesterol",
    v: "",
    u: "mg/dL",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_014",
      "ToshibaAIService_E_062",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_062"],
    min: 20,
    max: 1000,
  },
  {
    k: "maximum_blood_pressure",
    v: "",
    u: "mmHg",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_015",
      "ToshibaAIService_E_063",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_063"],
    min: 60,
    max: 300,
  },
  {
    k: "minimum_blood_pressure",
    v: "",
    u: "mmHg",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_016",
      "ToshibaAIService_E_064",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_064"],
    min: 30,
    max: 150,
  },
  {
    k: "height",
    v: "",
    u: "cm",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_017",
      "ToshibaAIService_E_065",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_065"],
    min: 100,
    max: 250,
  },

  {
    k: "body_weight",
    v: "",
    u: "kg",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_018",
      "ToshibaAIService_E_066",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_066"],
    min: 30,
    max: 200,
  },

  {
    k: "bmi",
    v: "",
    u: "",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_019",
      "ToshibaAIService_E_067",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_067"],
    min: 10,
    max: 100,
  },

  {
    k: "waist_circum",
    v: "",
    u: "cm",
    errorCodesForRequiredField: [
      "ToshibaAIService_E_020",
      "ToshibaAIService_E_068",
    ],
    errorCodesForInvalidRange: ["ToshibaAIService_E_068"],
    min: 40,
    max: 250,
  },
];

// @ts-ignore
const Home = ({ navigation }) => {
  //=========== HOOK ==========
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [data, setData] = useState<API.Item[]>([]);

  const [detailId, setDetailId] = useState<number | undefined>(undefined);
  const [aiInfos, setAiInfos] = useState<API.AiInfo[] | undefined>(
    undefined
    // [
    //   {
    //     k: "weight",
    //     v: "",
    //   },
    // ]
  );
  const [baseError, setBaseError] = useState<string | undefined>(undefined);
  const [isShowConfirmUseAi, setShowConfirmUseAi] = useState<boolean>(false);
  const [deleteRecordId, setDeleteRecordId] = useState<number | undefined>(
    undefined
  );
  const [isLoadingDetail, setLoadingDetail] = useState<boolean>(false);

  //=========== NAV ==========
  const openScreenResultIfNeed = (predictId: number | undefined) => {
    if (predictId !== undefined) {
      navigation.push("Result", { id: predictId });
    }
  };

  //=========== API - GET ALL ==========
  const { isLoading, error } = useQuery<API.getAll, Error>(
    "get-all",
    async () => {
      return await ApiServices.findAll();
    },
    {
      onSuccess: (res) => {
        setData(res.body.rows);
      },
      onError: (err: any) => {
        let message = toMessageError(err);
        ppw("findAll().onError().message", message);
        setBaseError(message);
      },
    }
  );

  //=========== API - DETAIL ==========
  const callApiGetDetail = async (id: number): Promise<API.DataHealths> => {
    try {
      setLoadingDetail(true);
      let res = await ApiServices.findDetail(id);
      // @ts-ignore
      return res?.body?.data_healths ?? {};
    } catch (e) {
      return {};
    } finally {
      setLoadingDetail(false);
    }
  };

  //=========== API - PREDICT ==========
  const predictMutation = useMutation((req) => ApiServices.getPredict(req));
  const callApiGetPredict = async (req: API.PredictRequest) => {
    // @ts-ignore
    await predictMutation.mutateAsync(req, {
      onSuccess: async (res: AxiosResponse<API.PredictResponse>) => {
        ppw("callApiGetPredict().onSuccess().res", res);
        await getPredictOnOk(res);
      },
      onError: async (err) => {
        let message = toMessageError(err);
        ppw("callApiGetPredict().onError().message", message);
        setBaseError(message);
        setAiInfos(undefined);
        setDetailId(undefined);
      },
    });
  };

  const getPredictOnOk = async (res: any) => {
    setAiInfos(undefined);
    setDetailId(undefined);
    await queryClient.invalidateQueries({ queryKey: ["get-all"] });
    let predictId = res?.data?.body?.toshiba_prediction_id;
    setTimeout((_) => {
      openScreenResultIfNeed(predictId);
    }, 1000);
  };

  const callApiGetPredictWithShowUpdateInfoPopup = async (
    req: API.PredictRequest
  ) => {
    // @ts-ignore
    await predictMutation.mutateAsync(req, {
      onSuccess: async (res) => {
        ppw("callApiGetPredictWithShowUpdateInfoPopup().onSuccess().res", res);
        await getPredictOnOk(res);
      },
      onError: async (err) => {
        // prepare update info popup data
        if (isAppError(err)) {
          // @ts-ignore
          let apiError = err?.errorCodes ?? [];

          let dataHealths: API.DataHealths = {};
          dataHealths = await callApiGetDetail(req.health_check_id);

          ppw(
            "callApiGetPredictWithShowUpdateInfoPopup().onError().apiError",
            apiError
          );

          let infoData = cloneDeep(AI_INFO_FORM_DATA);

          infoData.forEach((m) => {
            let fieldErrors = m.errorCodesForRequiredField;
            if (fieldErrors !== undefined) {
              let isApiErrorNeedFieldUpdate =
                fieldErrors?.some((e) => {
                  return apiError.includes(e);
                }) ?? false;

              // ppw(
              //   "callApiGetPredictWithShowUpdateInfoPopup().onError().isApiErrorNeedFieldUpdate",
              //   isApiErrorNeedFieldUpdate
              // );
              // ppw(
              //   "callApiGetPredictWithShowUpdateInfoPopup().onError().fieldErrors",
              //   fieldErrors
              // );
              m.isNeedUpdate = isApiErrorNeedFieldUpdate;

              // default value
              if (m.isNeedUpdate) {
                // @ts-ignore
                let oldDataOfField: string | undefined =
                  dataHealths[m.k] ?? undefined;
                ppw(
                  "callApiGetPredictWithShowUpdateInfoPopup().onError().oldDataOfField",
                  oldDataOfField
                );
                try {
                  if (
                    oldDataOfField !== undefined &&
                    oldDataOfField !== null &&
                    oldDataOfField !== "" &&
                    String(oldDataOfField).trim() !== "" &&
                    !isNaN(Number(oldDataOfField)) // is number
                  ) {
                    oldDataOfField = Number(oldDataOfField).toString();
                  } else {
                    oldDataOfField = undefined;
                  }
                } catch (e) {
                  oldDataOfField = undefined;
                }

                m.defaultValue = oldDataOfField;
                m.v = m.defaultValue ?? "";

                // validate
                let errorMessage = "";
                if (
                  m.errorCodesForInvalidRange !== undefined &&
                  !isEmpty(m.errorCodesForInvalidRange)
                ) {
                  errorMessage = t(`${m.errorCodesForInvalidRange[0]}`) ?? "";
                }

                try {
                  if (
                    String(m.v).trim() !== "" &&
                    m.min !== undefined &&
                    m.max !== undefined
                  ) {
                    let valueAsNumber = Number(m.v);
                    if (valueAsNumber < m.min || valueAsNumber > m.max) {
                      m.errorMessage = errorMessage;
                    } else {
                      m.errorMessage = "";
                    }
                  }
                } catch (e) {
                  m.errorMessage = "";
                }
              }
            }
          });

          let isLeastOneFieldNeedUpdate = infoData.some((m) => m.isNeedUpdate);

          if (isLeastOneFieldNeedUpdate) {
            setAiInfos(infoData);
          } else {
            setDetailId(undefined);
            let message = toMessageError(err);
            ppw(
              "callApiGetPredictWithShowUpdateInfoPopup().onError().message",
              message
            );
            setBaseError(message);
          }
        } else {
          setDetailId(undefined);
          let message = toMessageError(err);
          ppw(
            "callApiGetPredictWithShowUpdateInfoPopup().onError().message",
            message
          );
          setBaseError(message);
        }
      },
    });
  };

  //=========== API DELETE RECORD ==========
  const callApiDeleteRecord = async (req: API.DeleteRecordRequest) => {
    try {
      setLoadingDetail(true);
      await ApiServices.deleteRecordApi(req);
      await queryClient.invalidateQueries({ queryKey: ["get-all"] });
    } catch (e) {
      return {};
    } finally {
      setLoadingDetail(false);
    }
  };

  //=========== ERROR ==========
  if (error) return <Text></Text>;

  //=========== LISTENER - UPDATE INFO POPUP ==========
  let onOkAiUpdateInfoPopup = () => {
    // validate data
    let newInfo = cloneDeep(aiInfos);
    newInfo?.forEach((m) => {
      if (m.isNeedUpdate) {
        if (
          m.min != undefined &&
          m.max != undefined &&
          m.errorCodesForInvalidRange !== undefined &&
          !isEmpty(m.errorCodesForInvalidRange)
        ) {
          if (m.v !== undefined && m.v.trim() !== "") {
            let value = Number(m.v);
            if (m.min <= value && value <= m.max) {
              m.errorMessage = undefined;
            } else {
              ppw("onOkAiUpdateInfoPopup().errorAt: ", m);
              m.errorMessage = t(`${m.errorCodesForInvalidRange[0]}`) ?? "";
            }
          } else {
            ppw("onOkAiUpdateInfoPopup().errorAt: ", m);
            m.errorMessage = t(`${m.errorCodesForInvalidRange[0]}`) ?? "";
          }
        }
      } else {
        m.errorMessage = undefined;
      }
    });

    let isLeastOneHaveError =
      newInfo?.some((m) => m.isNeedUpdate && m.errorMessage !== undefined) ??
      false;

    setAiInfos(newInfo);

    if (isLeastOneHaveError) return;

    // prepare data for predict
    let req: any = {
      health_check_id: detailId,
      health_check_type: 1,
    };

    // insert info to req
    aiInfos?.forEach((m) => {
      if (m.isNeedUpdate) {
        req[m.k] = m.v;
      }
    });

    // call predict
    callApiGetPredict(req);
  };

  let onCancelAiUpdateInfoPopup = () => {
    setAiInfos(undefined);
    setDetailId(undefined);
  };

  let onChangeAiUpdateInfoPopup = (key: string, value: string) => {
    // new info
    let newInfo = cloneDeep(aiInfos);
    newInfo?.forEach((m) => {
      if (m.k === key) {
        m.v = value;
      }
    });

    // update info
    setAiInfos(newInfo);
  };

  //=========== LISTENER - ==========
  let onPressAiStatusImage = (
    isExistResult: boolean,
    detailId: number,
    predictId: number | undefined
  ) => {
    ppw("onPressAiStatusImage().detailId", detailId);

    if (isExistResult) {
      // open result screen
      navigation.push("Result", { id: predictId });
    } else {
      // prepare for predict
      setDetailId(detailId);
      setTimeout(() => {
        setShowConfirmUseAi(true);
      }, 500);
    }
  };

  let onOkPopupMessageError = () => {
    setBaseError(undefined);
  };

  let onPressRecycleBinImage = (detailId: number) => {
    ppw("onPressRecycleBinImage().detailId", detailId);
    setDeleteRecordId(detailId);
  };

  //=========== LISTENER - CONFIRM USE AI ==========
  let onOkPopupConfirmUseAi = () => {
    setTimeout(() => {
      if (detailId != null) {
        let req: API.PredictRequest = {
          health_check_id: detailId,
          health_check_type: 1,
        };
        callApiGetPredictWithShowUpdateInfoPopup(req);
      }
    }, 500);
    setShowConfirmUseAi(false);
  };

  let onCancelPopupConfirmUseAi = () => {
    setDetailId(undefined);
    setShowConfirmUseAi(false);
  };

  //=========== LISTENER - CONFIRM DELETE RECORD ==========
  let onOkPopupConfirmDeleteRecord = () => {
    if (!deleteRecordId) return;
    const req: API.DeleteRecordRequest = {
      id: deleteRecordId,
    };
    callApiDeleteRecord(req);
    setDeleteRecordId(undefined);
  };

  let onCancelPopupConfirmDeleteRecord = () => {
    setDeleteRecordId(undefined);
  };

  //=========== OUT ==========
  return (
    <Box display={"flex"} bg={Colors.white} flex={1}>
      {!isEmpty(aiInfos) && !predictMutation.isLoading && (
        <AiUpdateInfoPopup
          input={aiInfos}
          onOk={onOkAiUpdateInfoPopup}
          onCancel={onCancelAiUpdateInfoPopup}
          onChange={onChangeAiUpdateInfoPopup}
        />
      )}
      {baseError != null && (
        <PopupMessage
          isOpen={true}
          message={baseError}
          onOk={onOkPopupMessageError}
        />
      )}
      {isShowConfirmUseAi && (
        <PopupMessage
          isOpen={true}
          message={t("pleaseWait") ?? ""}
          onOk={onOkPopupConfirmUseAi}
          onCancel={onCancelPopupConfirmUseAi}
        />
      )}
      {deleteRecordId && (
        <PopupMessage
          isOpen={true}
          message={t("areYouSureForDelete") ?? ""}
          onOk={onOkPopupConfirmDeleteRecord}
          onCancel={onCancelPopupConfirmDeleteRecord}
        />
      )}
      {(isLoading || isLoadingDetail) && <LoadingView isOpen={true} />}
      {predictMutation.isLoading && <AiLoadingView />}
      <Divider />
      {!data || !isEmpty(data) ? (
        <FlatList
          data={data}
          keyExtractor={(item) => item.id.toString()}
          renderItem={({ item }) => {
            let isExistResult = item.toshiba_prediction_id !== null;
            let isSelected = item.id === detailId;
            return (
              <Pressable
                onPress={() =>
                  navigation.push("Detail", { id: item.id, isEditArg: false })
                }
                overflow="hidden"
              >
                <HStack
                  margin={4}
                  //   backgroundColor={Colors.primaryColor}
                  // backgroundColor={Colors.primaryColor}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Text
                    fontSize={16}
                    color={Colors.black}
                    fontStyle={isSelected ? "bold" : "normal"}
                    paddingRight={"8px"}
                  >
                    {item.file_name}
                  </Text>
                  <HStack>
                    <Pressable onPress={() => onPressRecycleBinImage(item.id)}>
                      <Image
                        id={"RecycleBin"}
                        style={{
                          width: 26,
                          height: 32,
                          marginRight: 16,
                        }}
                        source={ic_recycle_bin}
                      />
                    </Pressable>
                    <Pressable
                      onPress={() =>
                        onPressAiStatusImage(
                          isExistResult,
                          item.id,
                          item.toshiba_prediction_id
                        )
                      }
                    >
                      <Image
                        id={"AiStatus"}
                        style={{
                          width: 32,
                          height: 32,
                        }}
                        source={
                          isExistResult
                            ? ic_to_ai_report
                            : ic_to_ai_magnifying_glass
                        }
                      />
                    </Pressable>
                  </HStack>
                </HStack>
              </Pressable>
            );
          }}
          ItemSeparatorComponent={() => (
            <Box marginX={4}>
              <Divider />
            </Box>
          )}
        />
      ) : (
        <Box
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            // backgroundColor: Colors.redError,
          }}
        >
          <Text color={Colors.black}>{isLoading ? "" : t("No data")}</Text>
        </Box>
      )}
    </Box>
  );
};

export default Home;
